<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import Pagination from '../../Pagination.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import Table from '../../Table.svelte';
  import type { PaginationInfo } from '../../../lib/api/employee.service';
  import employerEmployeeService from '../../../lib/api/employer/employees';

  // ===== EMPLOYEES TAB STATE =====
  interface EmployeeData {
    uuid: string;
    email: string;
    email_verified_at: string;
    full_name: string;
    phone: string;
    credit_total: number;
    user_login_method: {
      uuid: string;
      name: string;
    };
    user_role: {
      uuid: string;
      name: string;
    };
    user_status: {
      uuid: string;
      name: string;
    };
    job_title: string;
    location: string;
    donation_requests: any[] | null;
  }
  
  interface EmployeeResponse {
    success: boolean;
    data: EmployeeData[] | {
      current_page: number;
      data: EmployeeData[];
      per_page: number;
      total: number;
      last_page: number;
    };
    paging?: {
      current_page: number;
      last_page: number;
      per_page: number;
      current_items: number;
      from: number;
      to: number;
      total: number;
    };
    message?: string;
  }

  let employees: EmployeeData[] = [];
  let employeesLoading = false;
  let employeesError: string | null = null;
  let employeesPaging: PaginationInfo = {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    current_items: 0,
    from: 0,
    to: 0,
    total: 0
  };
  let employeesSearchQuery = '';
  let employeesCurrentPage = 1;
  let employeesTotalPages = 1;
  let employeesItemsPerPage = 10;

  // ===== EMPLOYEES TAB FUNCTIONS =====
  async function loadEmployees() {
    employeesLoading = true;
    employeesError = null;
    
    try {
      const requestedPage = employeesCurrentPage;
      const filters: Record<string, any> = {
        page: requestedPage,
        per_page: employeesItemsPerPage,
      };
      
      if (employeesSearchQuery) {
        filters.phrase = employeesSearchQuery;
      }
      
      const response = await employerEmployeeService.getEmployees(filters) as EmployeeResponse;
      
      if (response.success && response.data) {
        if (Array.isArray(response.data)) {
          employees = response.data;
        }
        
        if (response.paging) {
          employeesPaging = {
            current_page: requestedPage,
            last_page: response.paging.last_page,
            per_page: response.paging.per_page,
            current_items: employees.length,
            from: (requestedPage - 1) * response.paging.per_page + 1,
            to: (requestedPage - 1) * response.paging.per_page + employees.length,
            total: response.paging.total
          };
          
          employeesTotalPages = response.paging.last_page || 1;
        }
      } else {
        employeesError = 'Failed to load employees';
        employees = [];
      }
    } catch (err) {
      console.error('Error loading employees:', err);
      employeesError = 'An error occurred while loading employees';
      employees = [];
    } finally {
      employeesLoading = false;
    }
  }

  function handleEmployeesSearch() {
    employeesCurrentPage = 1;
    loadEmployees();
  }

  function handleEmployeesPageChange(event: CustomEvent<{page: number}>) {
    employeesCurrentPage = event.detail.page;
    loadEmployees();
  }

  function viewEmployee(uuid: string) {
    push(`/employees/${uuid}`);
  }

  function formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  function getStatusClass(status: string): string {
    if (!status) return '';
    
    const statusLower = status.toLowerCase();
    if (statusLower === 'active') return 'status-active';
    if (statusLower === 'inactive') return 'status-inactive';
    return '';
  }

  function handleEmployeesRowClick(row: any) {
    if (row && row.uuid) {
      viewEmployee(row.uuid);
    }
  }

  // ===== TABLE COLUMNS =====
  const employeesColumns = [
    {
      key: 'full_name',
      label: 'Name',
      width: '25%'
    },
    {
      key: 'email',
      label: 'Email',
      width: '30%'
    },
    {
      key: 'total_donation_amount',
      label: 'Donation Total',
      width: '30%',
      render: (value: number) => {
        return `$${value.toFixed(2)}`
      }
    },
    {
      key: 'total_donated_hours',
      label: 'Donated Hours',
      width: '30%'
    },
    {
      key: 'total_pending_hours',
      label: 'Pending Hours',
      width: '30%'
    },
    {
      key: 'user_status',
      label: 'Status',
      width: '24%',
      align: 'center',
      render: (value: any, row: any) => {
        const status = row.user_status?.name || '-';
        const statusClass = getStatusClass(status);
        return `<span class="status-badge ${statusClass}">${status}</span>`;
      }
    }
  ];

  onMount(() => {
    loadEmployees();
  });
</script>

<div class="employees-unified-page">
  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-left">
      <SearchInput 
        placeholder="Search employees..." 
        bind:value={employeesSearchQuery}
        on:search={handleEmployeesSearch}
        width="250px"
      />
    </div>
  </div>

  {#if employeesLoading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading employees...</p>
    </div>
  {:else if employeesError}
    <div class="error-container">
      <p class="error-message">{employeesError}</p>
      <Button variant="primary" on:click={loadEmployees}>
        Try Again
      </Button>
    </div>
  {:else if employees.length === 0}
    <div class="empty-state">
      <p>No employees found</p>
      <Button variant="primary" on:click={() => { employeesSearchQuery = ''; loadEmployees(); }}>
        Reset Filters
      </Button>
    </div>
  {:else}
    <div class="table-section">
      <Table 
        columns={employeesColumns}
        data={employees}
        onRowClick={handleEmployeesRowClick}
      />
    </div>

    {#if employeesTotalPages > 1}
      <div class="pagination-container">
        <Pagination 
          currentPage={employeesCurrentPage} 
          totalPages={employeesTotalPages} 
          on:pageChange={handleEmployeesPageChange} 
        />
      </div>
    {/if}
  {/if}
</div>

<style>
  .employees-unified-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    background-color: #f7fafc;
    min-height: 100vh;
  }


  /* Filters Section */
  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    width: 100%;
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex: 1;
  }


  /* Table Section */
  .table-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }


  /* Loading/Error States */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid var(--color-primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: #dc2626;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }

  /* Status Badges */
  :global(.status-badge) {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    display: inline-block;
  }

  :global(.status-active) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.status-inactive) {
    background-color: #fee2e2;
    color: #b91c1c;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .employees-unified-page {
      padding: 1rem;
    }

    .filters-section {
      flex-direction: column;
      gap: 1rem;
      align-items: stretch;
    }

    .filters-left {
      flex-direction: column;
      gap: 0.75rem;
    }
  }
</style>

